import React from 'react';
import {NextSeo} from 'next-seo';
import {Footer, Navbar} from '../components';
import Link from 'next/link';

/**
 *
 */
export default function Index() {
  return (
    <>
      <NextSeo
        title={'Email Glossary for SaaS | The Open-Source Email Platform'}
        description={'Discover the most important email terms and definitions in our email glossary.'}
        openGraph={{
          title: 'Email Glossary for SaaS | The Open-Source Email Platform',
          description: 'Discover the most important email terms and definitions in our email glossary.',
        }}
        additionalMetaTags={[
          {
            property: 'title',
            content: 'Email Glossary for SaaS | The Open-Source Email Platform',
          },
        ]}
      />

      <Navbar />

      <div className={'mx-auto max-w-7xl px-8 sm:px-0'}>
        <main>
          <div className="relative mx-auto max-w-lg lg:max-w-4xl">
            <div className={'mb-4 flex flex-col items-center justify-center gap-y-2'}>
              <div className={'mt-9 text-center'}>
                <h1 className="text-3xl font-bold tracking-tight text-neutral-800 sm:text-5xl">
                  Email Glossary for SaaS
                </h1>
                <p className="mx-auto mt-2 max-w-lg text-lg text-neutral-500">
                  Discover the most important email terms and definitions in our email glossary.
                </p>
              </div>
            </div>

            <div className={'prose'}>
              <Link href={'/glossary/#a'}>
                <h2 id={'a'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  A
                </h2>
              </Link>

              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>API</strong> - Application Programming Interface, An API is a toolbox of rules, methods, and
                  tools that lets developers connect and interact with various software systems in their applications.{' '}
                  <br /> The Plunk API is a RESTful API that allows developers to send emails from their applications.
                </li>
              </ul>

              <Link href={'/glossary/#b'}>
                <h2 id={'b'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  B
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>Bounce</strong> - A bounce is an email that is returned to the sender because it cannot be
                  delivered but may be delivered at a later time. For example because the recipient’s inbox is full.
                </li>
                <li>
                  <strong>Bounce Rate</strong> - The bounce rate is the percentage of emails that were not delivered to
                  the recipient’s inbox.
                </li>
                <li>
                  <strong>Bulk Email</strong> - Bulk email is an email that is sent to multiple recipients.
                </li>
              </ul>

              <Link href={'/glossary/#c'}>
                <h2 id={'c'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  C
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>Click-through Rate (CTR)</strong> - The click-through rate is the percentage of people who
                  clicked on a link in an email.
                </li>
                <li>
                  <strong>Conversion Rate</strong> - The conversion rate is the percentage of people who clicked on a
                  link in an email and completed the desired action.
                </li>
                <li>
                  <strong>Client</strong> - A client is a software that is used to send and receive emails. For example
                  Gmail or Outlook.
                </li>
              </ul>

              <Link href={'/glossary/#d'}>
                <h2 id={'d'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  D
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>DKIM</strong> - DomainKeys Identified Mail, DKIM is an email authentication method that uses a
                  digital signature to verify the sender’s identity.
                </li>
                <li>
                  <strong>DMARC</strong> - Domain-based Message Authentication, Reporting & Conformance, DMARC is an
                  email authentication method that uses SPF and DKIM to verify the sender’s identity.
                </li>
              </ul>

              <Link href={'/glossary/#e'}>
                <h2 id={'e'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  E
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>ESP</strong> - Email Service Provider, An ESP is a company that provides email services.
                </li>
              </ul>

              <Link href={'/glossary/#f'}>
                <h2 id={'f'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  F
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>Feedback Loop</strong> - A feedback loop is a service that allows email senders to receive
                  notifications when their emails are marked as spam.
                </li>
              </ul>

              <Link href={'/glossary/#g'}>
                <h2 id={'g'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  G
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>GDPR</strong> - General Data Protection Regulation, GDPR is a regulation that protects the
                  privacy of individuals in the European Union.
                </li>
                <li>
                  <strong>Graymail</strong> - Graymail is an email that is not spam but is not wanted by the recipient.
                </li>
              </ul>

              <Link href={'/glossary/#h'}>
                <h2 id={'h'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  H
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>Hard Bounce</strong> - A hard bounce is an email that is returned to the sender because it
                  cannot be delivered and will never be delivered. For example because the email address is invalid.
                </li>
                <li>
                  <strong>HTML</strong> - Hypertext Markup Language, HTML is a markup language that is used to create
                  web pages and email layouts.
                </li>
                <li>
                  <strong>Headers</strong> - Email headers contain metadata about the email such as the sender,
                  recipient, subject, and date.
                </li>
              </ul>

              <Link href={'/glossary/#i'}>
                <h2 id={'i'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  I
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>IP Address</strong> - An IP address is a unique identifier for a device connected to the
                  internet.
                </li>
                <li>
                  <strong>IMAP</strong> - Internet Message Access Protocol, IMAP is a protocol that allows emails to be
                  accessed from a mail server.
                </li>
              </ul>

              <Link href={'/glossary/#j'}>
                <h2 id={'j'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  J
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>JSON</strong> - JavaScript Object Notation, JSON is a data format that is used to exchange
                  data between applications.
                </li>
              </ul>

              <Link href={'/glossary/#l'}>
                <h2 id={'l'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  L
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>List</strong> - An email list is a collection of email addresses that are used to send emails
                </li>
              </ul>

              <Link href={'/glossary/#m'}>
                <h2 id={'m'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  M
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>MTA</strong> - Mail Transfer Agent, An MTA is a software that transfers emails from one
                  computer to another.
                </li>
                <li>
                  <strong>MX Record</strong> - Mail Exchange Record, An MX record is a DNS record that specifies which
                  mail servers are responsible for receiving emails for a domain.
                </li>
                <li>
                  <strong>Marketing Email</strong> - Marketing email is an email that is sent to multiple recipients to
                  promote a product or service.
                </li>
                <li>
                  <strong>Marketing automation</strong> - Marketing automation is the process of using software to
                  automate marketing tasks such as sending emails.
                </li>
                <li>
                  <strong>MIME</strong> - Multipurpose Internet Mail Extensions, MIME is a protocol that allows emails
                  to contain non-ASCII characters and attachments.
                </li>
              </ul>

              <Link href={'/glossary/#o'}>
                <h2 id={'o'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  O
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>Open Rate</strong> - The open rate is the percentage of people who opened an email.
                </li>
              </ul>

              <Link href={'/glossary/#p'}>
                <h2 id={'p'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  P
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>Personalization</strong> - Personalization is the process of customizing an email for each
                  recipient.
                </li>
                <li>
                  <strong>Phishing</strong> - Phishing is a type of cyber attack that uses emails to steal sensitive
                  information such as passwords or credit card details.
                </li>
                <li>
                  <strong>POP3</strong> - Post Office Protocol 3, POP3 is a protocol that allows emails to be accessed
                  from a mail server.
                </li>
              </ul>

              <Link href={'/glossary/#r'}>
                <h2 id={'r'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  R
                </h2>
              </Link>
              <ul className={'list-inside list-disc '}>
                <li>
                  <strong>Reputation</strong> - An email sender’s reputation is a score that indicates how trustworthy
                  they are.
                </li>
                <li>
                  <strong>Reply-to</strong> - A header that specifies the email address that replies should be sent to.
                </li>
                <li>
                  <strong>Rate limit</strong> - A rate limit is a limit on the number of emails that can be sent in a
                  defined period of time.
                </li>
              </ul>

              <Link href={'/glossary/#s'}>
                <h2 id={'s'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  S
                </h2>
              </Link>
              <ul className={'list-inside list-disc'}>
                <li>
                  <strong>SMTP</strong> - Simple Mail Transfer Protocol, SMTP is a protocol that allows emails to be
                  sent over the internet.
                </li>
                <li>
                  <strong>SPF</strong> - Sender Policy Framework, SPF is an email authentication method that allows
                  email
                </li>
                <li>
                  <strong>Spam</strong> - Spam is unsolicited bulk email.
                </li>
              </ul>

              <Link href={'/glossary/#t'}>
                <h2 id={'t'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  T
                </h2>
              </Link>
              <ul className={'list-inside list-disc'}>
                <li>
                  <strong>Transactional Email</strong> - Transactional email is an email that is sent to an individual
                  based on a specific action they took.
                </li>
              </ul>

              <Link href={'/glossary/#u'}>
                <h2 id={'u'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  U
                </h2>
              </Link>
              <ul className={'list-inside list-disc'}>
                <li>
                  <strong>Unsubscribe</strong> - Unsubscribe is the process of removing a recipient from an email list.
                </li>
              </ul>

              <Link href={'/glossary/#v'}>
                <h2 id={'v'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  V
                </h2>
              </Link>
              <ul className={'list-inside list-disc'}>
                <li>
                  <strong>Verification</strong> - Verification is the process of confirming that an email address is
                  valid.
                </li>
              </ul>

              <Link href={'/glossary/#w'}>
                <h2 id={'w'} className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
                  W
                </h2>
              </Link>
              <ul className={'list-inside list-disc'}>
                <li>
                  <strong>Warmup</strong> - Warmup is the process of gradually increasing the number of emails sent from
                  a new IP address to establish a good reputation.
                </li>
                <li>
                  <strong>Webhooks</strong> - Webhooks are a way for apps to communicate with each other in real-time.
                  In email software, webhooks are indispensable for developers, as they provide the means to react to
                  and handle important email-related events as they occur.
                </li>
              </ul>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
}
